import { create } from 'zustand';
import { persist, type PersistStorage, type StorageValue } from 'zustand/middleware';
import * as idb from 'idb-keyval';
import type { ToolRound } from '../../../shared/ipc';

export type ChatRole = 'user' | 'assistant' | 'system';

export type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
  createdAt: number;
  // Set on assistant messages: the intermediate tool-loop turns from the
  // Anthropic call. Replayed verbatim on the next chat() so handles like
  // h_3 stay alive across user turns. Capped at the last 6 user/assistant
  // pairs when the renderer rebuilds history.
  toolRounds?: ToolRound[];
  // Streaming flag. While true, the renderer appends text deltas to `content`.
  // Cleared when the chat() promise resolves and the message is finalized.
  streaming?: boolean;
};

export const STREAMING_MESSAGE_ID = '__streaming__';

export type ChatConversation = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
};

type ChatState = {
  conversations: ChatConversation[];
  currentConversationId: string | null;
  sending: boolean;
  model: string | null;
  models: Array<{ id: string; label: string }>;
  agentReady: boolean;
  agentError: string | null;
  setAgentConfig: (cfg: { defaultModel: string; models: Array<{ id: string; label: string }> }) => void;
  setAgentError: (error: string) => void;
  setModel: (id: string) => void;
  setCurrentConversation: (id: string) => void;
  startNewChat: () => string;
  pushMessage: (msg: Omit<ChatMessage, 'id' | 'createdAt'> & { id?: string }) => ChatMessage;
  beginStreamingAssistant: () => void;
  appendStreamingDelta: (delta: string) => void;
  finalizeStreamingMessage: (final: { content: string; toolRounds?: ToolRound[] }) => void;
  discardStreamingMessage: () => void;
  setSending: (sending: boolean) => void;
  resetCurrentChat: () => void;
  resetAllChats: () => void;
  pruneExpiredConversations: (now?: number) => void;
  bootstrapOnLaunch: () => void;
};

type Persisted = Pick<ChatState, 'conversations' | 'currentConversationId' | 'model'>;

type LegacyPersisted = {
  messages?: ChatMessage[];
  model?: string | null;
};

const MAX_CHAT_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const NEW_CHAT_TITLE = 'New chat';
let didBootstrapThisRuntime = false;

const idbStorage: PersistStorage<Persisted> = {
  getItem: async (name) => {
    const v = await idb.get<StorageValue<Persisted>>(name);
    return v ?? null;
  },
  setItem: async (name, value) => {
    await idb.set(name, value);
  },
  removeItem: async (name) => {
    await idb.del(name);
  },
};

let counter = 0;
function nextId(): string {
  counter += 1;
  return `m_${Date.now().toString(36)}_${counter.toString(36)}`;
}

let conversationCounter = 0;
function nextConversationId(): string {
  conversationCounter += 1;
  return `c_${Date.now().toString(36)}_${conversationCounter.toString(36)}`;
}

function clipTitle(text: string): string {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return NEW_CHAT_TITLE;
  return cleaned.length <= 70 ? cleaned : `${cleaned.slice(0, 67)}...`;
}

function createConversation(now = Date.now()): ChatConversation {
  return {
    id: nextConversationId(),
    title: NEW_CHAT_TITLE,
    createdAt: now,
    updatedAt: now,
    messages: [],
  };
}

function pruneConversations(conversations: ChatConversation[], now = Date.now()): ChatConversation[] {
  const cutoff = now - MAX_CHAT_AGE_MS;
  return conversations
    .filter((c) => c.updatedAt >= cutoff)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

function normalizePersisted(state: Persisted | LegacyPersisted | undefined): Persisted {
  if (!state) {
    return { conversations: [], currentConversationId: null, model: null };
  }

  if ('conversations' in state && Array.isArray(state.conversations)) {
    return {
      conversations: state.conversations,
      currentConversationId: state.currentConversationId ?? null,
      model: state.model ?? null,
    };
  }

  const legacyState = state as LegacyPersisted;
  const legacyMessages = Array.isArray(legacyState.messages) ? legacyState.messages : [];
  const now = Date.now();
  const migrated = legacyMessages.length
    ? [
        {
          id: nextConversationId(),
          title: clipTitle(
            legacyMessages.find((m: ChatMessage) => m.role === 'user')?.content ?? NEW_CHAT_TITLE,
          ),
          createdAt: legacyMessages[0]?.createdAt ?? now,
          updatedAt: legacyMessages[legacyMessages.length - 1]?.createdAt ?? now,
          messages: legacyMessages,
        },
      ]
    : [];

  return {
    conversations: migrated,
    currentConversationId: migrated[0]?.id ?? null,
    model: legacyState.model ?? null,
  };
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      conversations: [],
      currentConversationId: null,
      sending: false,
      model: null,
      models: [],
      agentReady: false,
      agentError: null,
      setAgentConfig: ({ defaultModel, models }) =>
        set((s) => ({
          agentReady: true,
          models,
          model: s.model ?? defaultModel,
        })),
      setAgentError: (error) => set({ agentError: error, agentReady: false }),
      setModel: (id) => set({ model: id }),
      setCurrentConversation: (id) =>
        set((s) => {
          if (!s.conversations.some((c) => c.id === id)) return {};
          return { currentConversationId: id };
        }),
      startNewChat: () => {
        const now = Date.now();
        const created = createConversation(now);
        set((s) => ({
          conversations: [created, ...pruneConversations(s.conversations, now)],
          currentConversationId: created.id,
          sending: false,
        }));
        return created.id;
      },
      pushMessage: (msg) => {
        const state = get();
        const now = Date.now();
        const currentId = state.currentConversationId ?? state.startNewChat();

        const full: ChatMessage = {
          id: msg.id ?? nextId(),
          role: msg.role,
          content: msg.content,
          createdAt: now,
          toolRounds: msg.toolRounds,
          streaming: msg.streaming,
        };

        set((s) => ({
          conversations: pruneConversations(
            s.conversations.map((c) => {
              if (c.id !== currentId) return c;
              const nextMessages = [...c.messages, full];
              const nextTitle =
                c.title === NEW_CHAT_TITLE && full.role === 'user' ? clipTitle(full.content) : c.title;
              return {
                ...c,
                title: nextTitle,
                updatedAt: now,
                messages: nextMessages,
              };
            }),
            now,
          ),
          currentConversationId: currentId,
        }));
        return full;
      },
      beginStreamingAssistant: () => {
        const now = Date.now();
        const currentId = get().currentConversationId ?? get().startNewChat();
        set((s) => ({
          conversations: s.conversations.map((c) => {
            if (c.id !== currentId) return c;
            // Drop any leftover streaming message before starting a new one.
            const cleaned = c.messages.filter((m) => m.id !== STREAMING_MESSAGE_ID);
            const placeholder: ChatMessage = {
              id: STREAMING_MESSAGE_ID,
              role: 'assistant',
              content: '',
              createdAt: now,
              streaming: true,
            };
            return { ...c, updatedAt: now, messages: [...cleaned, placeholder] };
          }),
          currentConversationId: currentId,
        }));
      },
      appendStreamingDelta: (delta) => {
        if (!delta) return;
        set((s) => ({
          conversations: s.conversations.map((c) => {
            if (c.id !== s.currentConversationId) return c;
            return {
              ...c,
              messages: c.messages.map((m) =>
                m.id === STREAMING_MESSAGE_ID
                  ? { ...m, content: m.content + delta }
                  : m,
              ),
            };
          }),
        }));
      },
      finalizeStreamingMessage: ({ content, toolRounds }) => {
        const now = Date.now();
        set((s) => ({
          conversations: s.conversations.map((c) => {
            if (c.id !== s.currentConversationId) return c;
            return {
              ...c,
              updatedAt: now,
              messages: c.messages.map((m) =>
                m.id === STREAMING_MESSAGE_ID
                  ? {
                      ...m,
                      id: nextId(),
                      content: content || m.content,
                      streaming: false,
                      toolRounds,
                    }
                  : m,
              ),
            };
          }),
        }));
      },
      discardStreamingMessage: () => {
        set((s) => ({
          conversations: s.conversations.map((c) => {
            if (c.id !== s.currentConversationId) return c;
            return {
              ...c,
              messages: c.messages.filter((m) => m.id !== STREAMING_MESSAGE_ID),
            };
          }),
        }));
      },
      setSending: (sending) => set({ sending }),
      resetCurrentChat: () =>
        set((s) => ({
          conversations: s.conversations.map((c) =>
            c.id === s.currentConversationId
              ? { ...c, messages: [], title: NEW_CHAT_TITLE, updatedAt: Date.now() }
              : c,
          ),
          sending: false,
        })),
      resetAllChats: () => {
        const created = createConversation();
        set({ conversations: [created], currentConversationId: created.id, sending: false });
      },
      pruneExpiredConversations: (now = Date.now()) =>
        set((s) => {
          const pruned = pruneConversations(s.conversations, now);
          const currentConversationId = pruned.some((c) => c.id === s.currentConversationId)
            ? s.currentConversationId
            : pruned[0]?.id ?? null;
          return {
            conversations: pruned,
            currentConversationId,
          };
        }),
      bootstrapOnLaunch: () => {
        if (didBootstrapThisRuntime) return;
        didBootstrapThisRuntime = true;
        const now = Date.now();
        set((s) => {
          const pruned = pruneConversations(s.conversations, now);
          const fresh = createConversation(now);
          return {
            conversations: [fresh, ...pruned],
            currentConversationId: fresh.id,
            sending: false,
          };
        });
      },
    }),
    {
      name: 'rcmc-chat',
      storage: idbStorage,
      version: 3,
      migrate: (persistedState) => {
        const normalized = normalizePersisted(persistedState as Persisted | LegacyPersisted);
        // v2 → v3: nothing to add (toolRounds defaults to undefined). But strip
        // any leftover streaming placeholder from a previous crashed session.
        return {
          ...normalized,
          conversations: normalized.conversations.map((c) => ({
            ...c,
            messages: c.messages.filter((m) => m.id !== STREAMING_MESSAGE_ID),
          })),
        };
      },
      partialize: (s): Persisted => ({
        conversations: s.conversations,
        currentConversationId: s.currentConversationId,
        model: s.model,
      }),
    },
  ),
);
